import {Injectable} from '@angular/core';
import {AuthService} from './auth.service';
import {LanguageService} from './language.service';
import {ThemeService} from './theme.service';
import {ContractorService} from './contractor.service';
import {LocationService} from './native/location.service';
import {InstallationService} from './native/installation.service';
import {PreferencesService} from './preferences.service';
import {StorageService} from '@twict4fiber/storage';
import {
  SK_1_AUTH_ACCESS_TOKEN,
  SK_1_AUTH_JWT_PAYLOAD,
  SK_1_AUTH_PIN_CODE,
  SK_1_AUTH_REFRESH_TOKEN,
  SK_1_DATA_PHOTOS_PHOTO_UUID,
  SK_1_DATA_SYNCHRONIZATIONS_SYNCHRONIZATION_UUID,
  SK_1_INFO_DATABASE_VERSION,
  SK_1_SETTINGS_CONTRACTOR_AVAILABLE_CONTRACTORS,
  SK_1_SETTINGS_CONTRACTOR_SELECTED_CONTRACTOR,
  SK_1_SETTINGS_PREFERENCES_HIDE_FUTURE_TASKS,
  SK_1_SETTINGS_PREFERENCES_LOCALE,
  SK_1_SETTINGS_PREFERENCES_MAP_SERVICE,
  SK_1_SETTINGS_PREFERENCES_THEME,
  SK_1_SETTINGS_TASK_SELECTED_DPS,
  SK_1_SETTINGS_TASK_SELECTED_POPS,
  SK_1_SETTINGS_TASK_SELECTED_PROJECTS,
  SK_1_SETTINGS_TASK_SELECTED_TASK_TYPES
} from '../constants/storage-keys';
import {OldStorageService} from './native/old-storage.service';
import {StorageKey} from '../enums/storage-key';
import {promise} from '@twict4fiber/utils';

@Injectable({
  providedIn: 'root',
})
export class InitializationService {

  public constructor(private locationService: LocationService,
                     private authService: AuthService,
                     private languageService: LanguageService,
                     private themeService: ThemeService,
                     private contractorService: ContractorService,
                     private installationService: InstallationService,
                     private preferencesService: PreferencesService,
                     private storageService: StorageService,
                     private oldStorageService: OldStorageService) {
    window.addEventListener('beforeinstallprompt', (beforeInstallPromptEvent: any) => {
      beforeInstallPromptEvent.preventDefault();
      this.initializeInstallationService(beforeInstallPromptEvent);
    });
  }

  public async initializeApplication(): Promise<void> {
    await this.migrate();

    await Promise.all([
      this.initializeLocationService(),
      this.initializeAuthService(),
      this.initializeContractorService().then(async (): Promise<void> => {
        await Promise.all([
          this.initializeThemeService(),
          this.initializeLanguageService(),
        ]);
      }),
      this.preferencesService.initialize(),
    ]);
  }

  private async initializeLocationService(): Promise<void> {
    await this.locationService.initialize();
  }

  private async initializeAuthService(): Promise<void> {
    await this.authService.initialize();
  }

  private async initializeContractorService(): Promise<void> {
    await this.contractorService.initialize();
  }

  private async initializeLanguageService(): Promise<void> {
    await this.languageService.initialize();
  }

  private async initializeThemeService(): Promise<void> {
    await this.themeService.initialize();
  }

  private initializeInstallationService(beforeInstallPromptEvent: any): void {
    this.installationService.initialize(beforeInstallPromptEvent);
  }

  private async migrate(): Promise<void> {
    const databaseVersion = await this.storageService.getItem(SK_1_INFO_DATABASE_VERSION);

    if (!databaseVersion) {
      // Migrate Capacitor Preferences key value pairs
      const oldLocale = await this.oldStorageService.get(StorageKey.preferredLocale);
      if (oldLocale) {
        await this.storageService.setItem(SK_1_SETTINGS_PREFERENCES_LOCALE, oldLocale);
        await this.oldStorageService.remove(StorageKey.preferredLocale);
      }
      const oldTheme = await this.oldStorageService.get(StorageKey.preferredThemeId);
      if (oldTheme) {
        await this.storageService.setItem(SK_1_SETTINGS_PREFERENCES_THEME, oldTheme);
        await this.oldStorageService.remove(StorageKey.preferredThemeId);
      }
      const oldSelectedContractor = await this.oldStorageService.get(StorageKey.selectedContractorCode);
      if (oldSelectedContractor) {
        await this.storageService.setItem(SK_1_SETTINGS_CONTRACTOR_SELECTED_CONTRACTOR, oldSelectedContractor);
        await this.oldStorageService.remove(StorageKey.selectedContractorCode);
      }
      const oldAvailableContractors = await this.oldStorageService.get(StorageKey.availableContractorCodes);
      if (oldAvailableContractors) {
        await this.storageService.setItem(SK_1_SETTINGS_CONTRACTOR_AVAILABLE_CONTRACTORS, oldAvailableContractors);
        await this.oldStorageService.remove(StorageKey.availableContractorCodes);
      }
      const oldAccessToken = await this.oldStorageService.get(StorageKey.encryptedAccessToken);
      if (oldAccessToken) {
        await this.storageService.setItem(SK_1_AUTH_ACCESS_TOKEN, oldAccessToken);
        await this.oldStorageService.remove(StorageKey.encryptedAccessToken);
      }
      const oldRefreshToken = await this.oldStorageService.get(StorageKey.encryptedRefreshToken);
      if (oldRefreshToken) {
        await this.storageService.setItem(SK_1_AUTH_REFRESH_TOKEN, oldRefreshToken);
        await this.oldStorageService.remove(StorageKey.encryptedRefreshToken);
      }
      const oldPinCode = await this.oldStorageService.get(StorageKey.hashedPinCode);
      if (oldPinCode) {
        await this.storageService.setItem(SK_1_AUTH_PIN_CODE, oldPinCode);
        await this.oldStorageService.remove(StorageKey.hashedPinCode);
      }
      const oldJwtPayload = await this.oldStorageService.get(StorageKey.jwtPayload);
      if (oldJwtPayload) {
        await this.storageService.setItem(SK_1_AUTH_JWT_PAYLOAD, oldJwtPayload);
        await this.oldStorageService.remove(StorageKey.jwtPayload);
      }
      const oldHideFutureTasks = await this.oldStorageService.get(StorageKey.hideFutureTasks);
      if (oldHideFutureTasks) {
        await this.storageService.setItem(SK_1_SETTINGS_PREFERENCES_HIDE_FUTURE_TASKS, oldHideFutureTasks);
        await this.oldStorageService.remove(StorageKey.hideFutureTasks);
      }
      const oldMapService = await this.oldStorageService.get(StorageKey.navigationService);
      if (oldMapService) {
        await this.storageService.setItem(SK_1_SETTINGS_PREFERENCES_MAP_SERVICE, oldMapService);
        await this.oldStorageService.remove(StorageKey.navigationService);
      }
      const oldSelectedProjects = await this.oldStorageService.get(StorageKey.selectedProjectUuids);
      if (oldSelectedProjects) {
        await this.storageService.setItem(SK_1_SETTINGS_TASK_SELECTED_PROJECTS, oldSelectedProjects);
        await this.oldStorageService.remove(StorageKey.selectedProjectUuids);
      }
      const oldSelectedTaskTypes = await this.oldStorageService.get(StorageKey.selectedTaskTypeUuids);
      if (oldSelectedTaskTypes) {
        await this.storageService.setItem(SK_1_SETTINGS_TASK_SELECTED_TASK_TYPES, oldSelectedTaskTypes);
        await this.oldStorageService.remove(StorageKey.selectedTaskTypeUuids);
      }
      const oldSelectedPops = await this.oldStorageService.get(StorageKey.selectedAreaPopUuids);
      if (oldSelectedPops) {
        await this.storageService.setItem(SK_1_SETTINGS_TASK_SELECTED_POPS, oldSelectedPops);
        await this.oldStorageService.remove(StorageKey.selectedAreaPopUuids);
      }
      const oldSelectedDps = await this.oldStorageService.get(StorageKey.selectedDistributionPointUuids);
      if (oldSelectedDps) {
        await this.storageService.setItem(SK_1_SETTINGS_TASK_SELECTED_DPS, oldSelectedDps);
        await this.oldStorageService.remove(StorageKey.selectedDistributionPointUuids);
      }

      // Migrate Local Database
      try {
        await promise<void>(async (resolve, reject) => {
          try {
            const databases = await indexedDB.databases();
            const oldDatabaseInfo = databases?.find(d => d.name === 'twict4fiber-app-ionic-database')

            if (oldDatabaseInfo) {
              const oldDatabase = await new Promise<IDBDatabase>(resolve => {
                const request = indexedDB.open(oldDatabaseInfo.name, oldDatabaseInfo.version);
                request.onsuccess = () => resolve(request.result);
              });

              // Don't migrate tasks
              // if (oldDatabase.objectStoreNames.contains('tasks')) {
              //   const olds = await new Promise<any[]>(resolve => {
              //     const transaction = oldDatabase.transaction('tasks');
              //     const objectStore = transaction.objectStore('tasks');
              //     const request = objectStore.openCursor();
              //     const olds = [];
              //
              //     request.onsuccess = () => {
              //       const cursor = request.result;
              //
              //       if (cursor && cursor.primaryKey && cursor.value) {
              //         olds.push({key: cursor.primaryKey, value: cursor.value});
              //         cursor.continue();
              //       } else {
              //         resolve(olds);
              //       }
              //     };
              //   });
              //
              //   for (const old of olds) {
              //     if (old && old.key && old.value) {
              //       await this.storageService.setItem(SK_1_DATA_TASKS_TASK_UUID(old.key), old.value);
              //     }
              //   }
              // }

              if (oldDatabase.objectStoreNames.contains('photos')) {
                const olds = await new Promise<any[]>(resolve => {
                  const transaction = oldDatabase.transaction('photos');
                  const objectStore = transaction.objectStore('photos');
                  const request = objectStore.openCursor();
                  const olds = [];

                  request.onsuccess = () => {
                    const cursor = request.result;

                    if (cursor && cursor.primaryKey && cursor.value) {
                      olds.push({key: cursor.primaryKey, value: cursor.value});
                      cursor.continue();
                    } else {
                      resolve(olds);
                    }
                  };
                });

                for (const old of olds) {
                  if (old && old.key && old.value) {
                    await this.storageService.setItem(SK_1_DATA_PHOTOS_PHOTO_UUID(old.key), old.value);
                  }
                }
              }

              if (oldDatabase.objectStoreNames.contains('synchronizationActions')) {
                const olds = await new Promise<any[]>(resolve => {
                  const transaction = oldDatabase.transaction('synchronizationActions');
                  const objectStore = transaction.objectStore('synchronizationActions');
                  const request = objectStore.openCursor();
                  const olds = [];

                  request.onsuccess = () => {
                    const cursor = request.result;

                    if (cursor && cursor.primaryKey && cursor.value) {
                      olds.push({key: cursor.primaryKey, value: cursor.value});
                      cursor.continue();
                    } else {
                      resolve(olds);
                    }
                  };
                });

                for (const old of olds) {
                  if (old && old.key && old.value) {
                    old.value.uuid = old.key;

                    if (old.value.createdAt) {
                      old.value.createdAt = new Date(old.value.createdAt);
                    }

                    if (old.value.readyAt) {
                      old.value.readyAt = new Date(old.value.readyAt);
                    }

                    if (!old.value.completedAt && !old.value.failedAt) {
                      await this.storageService.setItem(SK_1_DATA_SYNCHRONIZATIONS_SYNCHRONIZATION_UUID(old.key), old.value);
                    }
                  }
                }
              }

              // Don't migrate stateDiagrams
              // if (oldDatabase.objectStoreNames.contains('stateDiagrams')) {
              //   const olds = await new Promise<any[]>(resolve => {
              //     const transaction = oldDatabase.transaction('stateDiagrams');
              //     const objectStore = transaction.objectStore('stateDiagrams');
              //     const request = objectStore.openCursor();
              //     const olds = [];
              //
              //     request.onsuccess = () => {
              //       const cursor = request.result;
              //
              //       if (cursor && cursor.primaryKey && cursor.value) {
              //         olds.push({key: cursor.primaryKey, value: cursor.value});
              //         cursor.continue();
              //       } else {
              //         resolve(olds);
              //       }
              //     };
              //   });
              //
              //   for (const old of olds) {
              //     if (old && old.key && old.value) {
              //       await this.storageService.setItem(SK_1_DATA_STATE_DIAGRAMS_STATE_DIAGRAM_UUID(old.key), old.value);
              //     }
              //   }
              // }

              // Don't migrate formTemplates
              // if (oldDatabase.objectStoreNames.contains('formTemplates')) {
              //   const olds = await new Promise<any[]>(resolve => {
              //     const transaction = oldDatabase.transaction('formTemplates');
              //     const objectStore = transaction.objectStore('formTemplates');
              //     const request = objectStore.openCursor();
              //     const olds = [];
              //
              //     request.onsuccess = () => {
              //       const cursor = request.result;
              //
              //       if (cursor && cursor.primaryKey && cursor.value) {
              //         olds.push({key: cursor.primaryKey, value: cursor.value});
              //         cursor.continue();
              //       } else {
              //         resolve(olds);
              //       }
              //     };
              //   });
              //
              //   for (const old of olds) {
              //     if (old && old.key && old.value) {
              //       await this.storageService.setItem(SK_1_DATA_FORM_TEMPLATES_FORM_TEMPLATE_UUID(old.key), old.value);
              //     }
              //   }
              // }

              // Don't migrate formDatas (user input is saved in synchronizations so no work is lost)
              // if (oldDatabase.objectStoreNames.contains('formDatas')) {
              //   const olds = await new Promise<any[]>(resolve => {
              //     const transaction = oldDatabase.transaction('formDatas');
              //     const objectStore = transaction.objectStore('formDatas');
              //     const request = objectStore.openCursor();
              //     const olds = [];
              //
              //     request.onsuccess = () => {
              //       const cursor = request.result;
              //
              //       if (cursor && cursor.primaryKey && cursor.value) {
              //         olds.push({key: cursor.primaryKey, value: cursor.value});
              //         cursor.continue();
              //       } else {
              //         resolve(olds);
              //       }
              //     };
              //   });
              //
              //   for (const old of olds) {
              //     if (old && old.key && old.value) {
              //       if (old.value.taskUuid) {
              //         await this.storageService.setItem(SK_1_DATA_FORM_DATA_TASK_UUID_FORM_DATA_UUID(old.value.taskUuid, old.key), old.value);
              //       }
              //     }
              //   }
              // }

              oldDatabase.close();
              await new Promise<void>(resolve => {
                const request = indexedDB.deleteDatabase(oldDatabaseInfo.name);
                request.onsuccess = () => resolve();
              });
            }
          } catch (err) {
            reject(err);
          }

          resolve();
        }, 10000); // timeout after 10 seconds
      } catch {
      }

      await this.storageService.setItem(SK_1_INFO_DATABASE_VERSION, 1);
      location.reload();
    }
  }

}
